import { useCallback, useEffect } from 'react';

import { useUiPreferencesForUser } from '@hcs/huell';
import { useLocalStorageState } from '@hcs/local-storage-state';
import { PreferencesKeys } from '@hcs/types';

import {
  HcMapChangeVectileGroupArgs,
  HcMapDisableMarkerClustersArgs,
  HcMapShowMonochromeArgs,
} from '../rtk';
import { HcMapPreferences } from '../types';

// Temporary hook for populating the new localStorage-based map preferences
// from the legacy Huell-based user preferences.
export const useMigrateLegacyMapPreferences = (mapId: string) => {
  const {
    state: mapPreferencesState,
    actions: { setMapPreferences },
  } = useHcMapPreferences(mapId);
  const { data: uiPreferences } = useUiPreferencesForUser();
  useEffect(() => {
    if (mapPreferencesState) return;
    const disableClustersLegacy =
      uiPreferences?.[PreferencesKeys.Maps]?.disableClusters;
    const soldMapLegacy =
      uiPreferences?.[PreferencesKeys.CompsMapSale]?.heatmap;
    const rentalMapLegacy =
      uiPreferences?.[PreferencesKeys.CompsMapSale]?.heatmap;
    let newPrefs: HcMapPreferences | null = null;
    if (
      disableClustersLegacy === undefined &&
      !soldMapLegacy &&
      !rentalMapLegacy
    ) {
      return;
    }
    switch (mapId) {
      case 'SUBJECT_MAP_ID':
      case 'COMP_SUMMARY_MAP_ID':
      case `REPORT_API/COMP_SELECTION_MAP-${'SOLD'}`:
      case `REPORT_API/COMPS_LIST_MAP-${'SOLD'}`: {
        if (soldMapLegacy || disableClustersLegacy !== undefined) {
          newPrefs = {
            heatmap: soldMapLegacy,
            markers: {
              disableClusters: disableClustersLegacy,
            },
          };
        }
        break;
      }
      case 'RENTAL_COMP_SUMMARY_MAP_ID':
      case `REPORT_API/COMP_SELECTION_MAP-${'RENTAL'}`:
      case `REPORT_API/COMPS_LIST_MAP-${'RENTAL'}`: {
        if (rentalMapLegacy || disableClustersLegacy !== undefined) {
          newPrefs = {
            heatmap: rentalMapLegacy,
            markers: {
              disableClusters: disableClustersLegacy,
            },
          };
        }
        break;
      }
    }
    if (newPrefs) {
      setMapPreferences(newPrefs);
    }
  }, [uiPreferences, mapPreferencesState, mapId, setMapPreferences]);
};

const STORAGE_KEY = 'hc-map-preferences';

export const useHcMapPreferences = (mapId: string) => {
  const {
    state: mapPreferenceState,
    actions: { setKeyValue, setValue, clear },
  } = useLocalStorageState<HcMapPreferences>(`${STORAGE_KEY}-${mapId}`);

  return {
    actions: {
      clear,
      setDisableMarkerClusters: useCallback(
        (payload: HcMapDisableMarkerClustersArgs) => {
          setKeyValue('markers', {
            ...mapPreferenceState?.markers,
            disableClusters: payload.disableClusters,
          });
        },
        [setKeyValue, mapPreferenceState],
      ),
      setHeatmapLayerPreference: useCallback(
        (payload: HcMapChangeVectileGroupArgs) => {
          if (payload.vectilesMetricGroup) {
            // save
            setKeyValue('heatmap', {
              ...mapPreferenceState?.heatmap,
              vectilesMetricGroup: payload.vectilesMetricGroup,
              vectilesMetricId: payload.vectilesMetricId,
            });
          } else {
            // remove
            setKeyValue('heatmap', {
              ...mapPreferenceState?.heatmap,
              vectilesMetricGroup: undefined,
              vectilesMetricId: undefined,
            });
          }
        },
        [setKeyValue, mapPreferenceState],
      ),
      setHeatmapsMonochromePreference: useCallback(
        (payload: HcMapShowMonochromeArgs) => {
          setKeyValue('heatmap', {
            ...mapPreferenceState?.heatmap,
            showMonochrome: payload.visible,
          });
        },
        [setKeyValue, mapPreferenceState],
      ),
      setMapPreferences: useCallback(
        (prefs: HcMapPreferences) => {
          setValue(prefs);
        },
        [setValue],
      ),
    },
    state: mapPreferenceState,
  };
};
